// src/components/map/MapCreditsFooter.jsx
//
// The credits footer along a map's bottom edge (footprint restyle S-e slice 2,
// DB 2026-11-02): SitePlanner's maps (PadPlannerMap) and Explore's main and
// compare maps (MapView, one footer per compare side). The map's canvas runs
// under it; the page's bottom items (the time strip, the readout) sit directly
// above it, by the height it reports (onHeight).
//
//   ┌──────────────────────────────────────────────────────────────────────┐
//   │ [mapbox logo]  (children: Paper's grid readout, the scale bar)       │
//   │                         Contains modified Copernic… © Mapbox © OSM … │
//   └──────────────────────────────────────────────────────────────────────┘
//
//   about 24 px   the Mapbox logo's height (88 × 23, mapbox-gl's own SVG, at
//                 its default size: never scaled, recolored or swapped for a
//                 compact form, as Mapbox's terms ask). Black at 55%.
//   credits       at the right, 10 px (never smaller), each a source's own
//                 (useMapCredits.js: the sources drawn now). The
//                 Copernicus line truncates, its full text in a tooltip;
//                 Mapbox's and OpenStreetMap's links and "Improve this map"
//                 never do, and are never put behind a button.
//   narrow        a footer under COMPACT_BELOW_PX (480 px) wraps to two
//                 lines: the logo and the children, then the credits.
//   Improve       "Improve this map" points at the camera on the style its
//                 source draws (feedbackHref), and follows each move.
//
// logo={false} credits={false}: Paper view, which draws no Mapbox tile and so
// owes nothing, keeps the footer for its grid readout and scale bar (DB's
// answer 7).
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { COMPACT_BELOW_PX, FEEDBACK_URL, MAPBOX_LOGO_URI, feedbackHref } from "../../lib/map/attribution";
import { isCopernicus, parseCredit } from "../../lib/map/mapCredits";
import { useMapCredits } from "./useMapCredits";
import "./mapCreditsFooter.css";

function improveHref(map, styleId) {
  const c = map?.getCenter?.();
  if (!styleId || !c) return null;
  return feedbackHref({ styleId, lng: c.lng, lat: c.lat, zoom: map.getZoom() });
}

function Credit({ map, credit }) {
  const parts = parseCredit(credit.html);
  return parts.map((p, i) => {
    if (!p.href) return <Fragment key={i}>{p.text}</Fragment>;
    const href = p.href.startsWith(FEEDBACK_URL) ? improveHref(map, credit.styleId) ?? p.href : p.href;
    const text = p.strong ? <strong>{p.text}</strong> : p.text;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer">{text}</a>;
  });
}

/** Mapbox's logo, its own SVG at 88 × 23, linking to Mapbox. */
export function MapboxLogo() {
  return (
    <a className="aip-credits-logo" href="https://www.mapbox.com/" target="_blank" rel="noopener noreferrer"
      aria-label="Mapbox" data-testid="mapbox-logo" style={{ backgroundImage: `url("${MAPBOX_LOGO_URI}")` }} />
  );
}

/** A map's drawn credits: Copernicus first (the one that truncates), then each source's own. */
export function CreditsText({ shown, map }) {
  if (shown.length === 0) return null;
  const copernicus = shown.filter((c) => isCopernicus(c.html));
  const rest = shown.filter((c) => !isCopernicus(c.html));
  return (
    <div className="aip-credits-text" data-testid="map-credits">
      {copernicus.map((c) => {
        const text = parseCredit(c.html).map((p) => p.text).join("");
        return <span key={c.html} className="aip-credit aip-credit--truncate" title={text} data-credit="copernicus"><Credit map={map} credit={c} /></span>;
      })}
      {rest.map((c) => (
        <span key={c.html} className="aip-credit" data-credit="source"><Credit map={map} credit={c} /></span>
      ))}
    </div>
  );
}

/**
 * @param {object} p
 * @param {object|null} p.map             the MapLibre map whose credits these are
 * @param {boolean} [p.logo]              Mapbox's logo, at the left (not on Paper)
 * @param {boolean} [p.credits]           the credits, at the right (not on Paper)
 * @param {import("react").ReactNode} [p.children]  between them: Paper's grid readout, the scale bar
 * @param {(px: number) => void} [p.onHeight]  the footer's height, for the page's bottom items to sit above
 * @param {object} [p.style]              placement
 * @param {string} [p.testid]
 */
export default function MapCreditsFooter({ map, logo = true, credits = true, children = null, onHeight = null, style = undefined, testid = "map-credits-footer" }) {
  const ref = useRef(null);
  const [narrow, setNarrow] = useState(false);
  // The map back from the hook only while it is live: nothing here calls into a removed map.
  const { credits: shown, map: live } = useMapCredits(credits ? map : null);
  const heightCb = useRef(onHeight);
  useLayoutEffect(() => { heightCb.current = onHeight; });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setNarrow(w > 0 && w < COMPACT_BELOW_PX);
      heightCb.current?.(Math.ceil(el.getBoundingClientRect().height));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="aip-credits-footer" data-chip-avoid data-testid={testid} data-narrow={narrow ? "" : undefined} style={style}>
      {logo && <MapboxLogo />}
      {children && <div className="aip-credits-middle">{children}</div>}
      {credits && <CreditsText shown={shown} map={live} />}
    </div>
  );
}
